import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useAllBusinesses } from './useSuperAdmin';
import { buildAdoptionRows, mapUsageDailyDoc } from '../utils/usageAdoption';
import { UsageBusinessAdoptionRow } from '../types/usageTracking';
import { useAuth } from '../contexts/AuthContext';

function dateYmdDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function useUsageAdoption(days = 7) {
  const { isSuperAdmin, isCSM } = useAuth();
  const { data: businesses = [], isLoading: businessesLoading } = useAllBusinesses();
  const since = dateYmdDaysAgo(Math.max(0, days - 1));

  const queryResult = useQuery({
    queryKey: [
      'usage',
      'adoption',
      days,
      since,
      businesses.map((b) => b.id).join(','),
    ],
    queryFn: async (): Promise<UsageBusinessAdoptionRow[]> => {
      const q = query(
        collection(db, COLLECTIONS.usageDaily),
        where('date', '>=', since)
      );
      const snap = await getDocs(q);
      const dailies = snap.docs.map((docSnap) =>
        mapUsageDailyDoc(docSnap.id, docSnap.data() as Record<string, unknown>)
      );
      return buildAdoptionRows(
        dailies,
        businesses.map((b) => ({ id: b.id, name: b.name }))
      );
    },
    enabled: (isSuperAdmin || isCSM) && !businessesLoading,
    staleTime: 60_000,
  });

  return {
    ...queryResult,
    businessesLoading,
  };
}
